import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  Shield, 
  Edit, 
  Trash2, 
  User, 
  Mail, 
  Phone, 
  CheckCircle, 
  XCircle,
  ChevronRight,
  ChevronDown,
  Send,
  Key,
  Truck
} from 'lucide-react';
import { permissionConfig } from '../config/permissions';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { employeesAPI } from '../services/api';

const EmployeeCard = ({ employee, onEdit, onDelete }) => {
  const [showPermissions, setShowPermissions] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { employees: employeePerms } = usePermissions();
  const { hasPermission, employee: currentUser } = useAuth();

  // Send verification email mutation
  const sendVerificationEmailMutation = useMutation(
    (uuid) => employeesAPI.sendVerificationEmail(uuid),
    {
      onSuccess: (response) => {
        toast.success(response.data.message || 'Verification email sent successfully!');
        queryClient.invalidateQueries(['employees']);
      },
      onError: (error) => {
        console.error('Send verification email error:', error);
        toast.error(error.response?.data?.message || 'Failed to send verification email');
      }
    }
  );

  // Send password reset email mutation
  const sendPasswordResetEmailMutation = useMutation(
    (uuid) => employeesAPI.sendPasswordResetEmail(uuid),
    {
      onSuccess: (response) => {
        toast.success(response.data.message || 'Password reset email sent successfully!');
        queryClient.invalidateQueries(['employees']);
      },
      onError: (error) => {
        console.error('Send password reset email error:', error);
        toast.error(error.response?.data?.message || 'Failed to send password reset email');
      }
    }
  );

  // Handle sending verification email
  const handleSendVerificationEmail = () => {
    if (!employee.is_active) {
      toast.error('Cannot send verification email to inactive employee');
      return;
    }
    
    if (employee.is_verified) {
      toast.error('Employee is already verified');
      return;
    }

    sendVerificationEmailMutation.mutate(employee.uuid);
  };

  // Handle sending password reset email
  const handleSendPasswordResetEmail = () => {
    if (!employee.is_active) {
      toast.error('Cannot send password reset email to inactive employee');
      return;
    }

    sendPasswordResetEmailMutation.mutate(employee.uuid);
  };

  const getRoleColor = (role) => {
    const colors = {
      developer: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-300',
      admin: 'bg-red-100 text-red-800 border-red-200',
      manager: 'bg-blue-100 text-blue-800 border-blue-200',
      staff: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPermissionsByModule = () => {
    const employeePermissions = employee.permissions?.map(p => p.name) || [];
    const moduleGroups = {};

    Object.entries(permissionConfig.modules).forEach(([moduleKey, module]) => {
      const modulePermissions = module.permissions.filter(p => 
        employeePermissions.includes(p.key)
      );
      
      if (modulePermissions.length > 0) {
        moduleGroups[moduleKey] = {
          ...module,
          permissions: modulePermissions,
          count: modulePermissions.length,
          total: module.permissions.length
        };
      }
    });

    return moduleGroups;
  };

  const moduleGroups = getPermissionsByModule();
  const totalPermissions = employee.permissions?.length || 0;

  return (
    <div className="card-modern group hover-lift animate-fade-in-up bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Employee Header */}
      <div className="card-header-responsive p-4 sm:p-5 lg:p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800">
        <div className="flex flex-col space-y-4">
          {/* Profile Section */}
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <div className="relative flex-shrink-0">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-glow transition-shadow">
                <User className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
              </div>
              {/* Status Indicator */}
              <div className={`absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-white dark:border-gray-800 ${
                employee.is_active ? 'bg-green-500' : 'bg-red-500'
              } shadow-sm`}></div>
            </div>
            
            <div className="min-w-0 flex-1">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white line-clamp-1 mb-1">
                {employee.name}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 line-clamp-1 mb-2">
                {employee.email}
              </p>
              {employee.phone && (
                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 dark:text-gray-500">
                  <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="line-clamp-1">{employee.phone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Status Badges Row */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {/* Role Badge */}
            <span className={`inline-flex items-center px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-full shadow-sm ${getRoleColor(employee.role)}`}>
              {employee.role.charAt(0).toUpperCase() + employee.role.slice(1)}
            </span>
            
            {/* Activity Status Badge */}
            <span className={`inline-flex items-center px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full shadow-sm ${
              employee.is_active
                ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                : 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
            }`}>
              {employee.is_active ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  Active
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  Inactive
                </>
              )}
            </span>
            
            {/* Verification Status Badge */}
            <span className={`inline-flex items-center px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full shadow-sm ${
              employee.is_verified
                ? 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
                : 'bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800'
            }`}>
              {employee.is_verified ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  Verified
                </>
              ) : (
                <>
                  <Mail className="w-3 h-3 mr-1.5 flex-shrink-0" />
                  Pending
                </>
              )}
            </span>

            {/* Permissions Count Badge */}
            <span className="inline-flex items-center px-3 py-1.5 text-xs sm:text-sm font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm">
              <Shield className="w-3 h-3 mr-1.5 flex-shrink-0" />
              {totalPermissions} {totalPermissions === 1 ? 'Permission' : 'Permissions'}
            </span>
          </div>
        </div>
      </div>

      {/* Employee Details */}
      <div className="p-4 sm:p-5 lg:p-6">
        {/* Email Actions Row - Mobile & Tablet */}
        {(employeePerms.canUpdate && (!employee.is_verified || employee.is_verified) && employee.is_active) && (
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
            {/* Send Verification Email */}
            {!employee.is_verified && (
              <button
                onClick={handleSendVerificationEmail}
                disabled={sendVerificationEmailMutation.isLoading}
                className="flex items-center justify-center px-4 py-2.5 text-sm font-medium text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
              >
                <Send className="w-4 h-4 mr-2 flex-shrink-0" />
                {sendVerificationEmailMutation.isLoading ? 'Sending Verification...' : 'Send Verification Email'}
              </button>
            )}
            
            {/* Send Password Reset Email */}
            {employee.is_verified && (
              <button
                onClick={handleSendPasswordResetEmail}
                disabled={sendPasswordResetEmailMutation.isLoading}
                className="flex items-center justify-center px-4 py-2.5 text-sm font-medium text-orange-700 bg-orange-50 dark:bg-orange-900/30 dark:text-orange-400 rounded-xl hover:bg-orange-100 dark:hover:bg-orange-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
              >
                <Key className="w-4 h-4 mr-2 flex-shrink-0" />
                {sendPasswordResetEmailMutation.isLoading ? 'Sending Reset...' : 'Send Password Reset'}
              </button>
            )}
          </div>
        )}

        {/* Permission Summary */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                Permissions Overview
              </h4>
            </div>
            <button
              onClick={() => setShowPermissions(!showPermissions)}
              className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              <span className="text-sm font-medium">{totalPermissions} Total</span>
              {showPermissions ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Module Overview Cards */}
          {Object.keys(moduleGroups).length > 0 ? (
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 mb-4">
              {Object.entries(moduleGroups).map(([moduleKey, moduleData]) => (
                <div
                  key={moduleKey}
                  className="bg-white dark:bg-gray-800 rounded-lg p-2.5 sm:p-3 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <span className="text-sm sm:text-base lg:text-lg flex-shrink-0">{moduleData.icon}</span>
                    <span className="text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      {moduleData.count}/{moduleData.total}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 line-clamp-1">
                    {moduleData.name}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Shield className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No permissions assigned</p>
              {(currentUser?.role === 'developer' || hasPermission('employees_assign_add')) && (
                <button
                  onClick={() => navigate(`/employees/${employee.uuid}/permissions`)}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                >
                  Assign permissions →
                </button>
              )}
            </div>
          )}

          {/* Detailed Permissions Expandable */}
          {showPermissions && Object.keys(moduleGroups).length > 0 && (
            <div className="mt-4 space-y-3 max-h-80 overflow-y-auto scrollbar-thin">
              {Object.entries(moduleGroups).map(([moduleKey, moduleData]) => (
                <div key={moduleKey} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-lg flex-shrink-0">{moduleData.icon}</span>
                    <h5 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base line-clamp-1">
                      {moduleData.name}
                    </h5>
                    <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full ml-auto">
                      {moduleData.count} of {moduleData.total}
                    </span>
                  </div>
                  <div className="grid gap-2">
                    {moduleData.permissions.map((permission) => (
                      <div key={permission.key} className="flex items-center space-x-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300 line-clamp-1">{permission.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons - Always Visible */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Permission Management */}
            {(currentUser?.role === 'developer' || hasPermission('employees_assign_view') || hasPermission('employees_assign_add') || hasPermission('employees_assign_delete')) && (
              <button
                onClick={() => navigate(`/employees/${employee.uuid}/permissions`)}
                className="flex items-center justify-center space-x-2 px-3 py-2.5 text-sm font-medium text-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-all shadow-sm hover:shadow-md border border-indigo-200 dark:border-indigo-800 w-full"
              >
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Permissions</span>
              </button>
            )}
            
            {/* Edit Employee */}
            {employeePerms.canUpdate && (
              <button
                onClick={() => onEdit(employee)}
                className="flex items-center justify-center space-x-2 px-3 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all shadow-sm hover:shadow-md border border-blue-200 dark:border-blue-800 w-full"
              >
                <Edit className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Edit</span>
              </button>
            )}
            
            {/* Delete Employee */}
            {employeePerms.canDelete && (
              <button
                onClick={() => onDelete(employee.uuid)}
                className="flex items-center justify-center space-x-2 px-3 py-2.5 text-sm font-medium text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-all shadow-sm hover:shadow-md border border-red-200 dark:border-red-800 w-full"
              >
                <Trash2 className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Delete</span>
              </button>
            )}
          </div>

          {/* Quick Info Row */}
          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="truncate">ID: {employee.uuid?.slice(-8).toUpperCase()}</span>
              <span className="truncate">Updated: {new Date(employee.updated_at || employee.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeCard;